import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * How a partner's program terms read in the current language: the rev-share rate as a percentage
 * with up to two decimals and trailing zeros trimmed ("20.00" -> 20%, "12.50" -> 12.5%; "12,5 %"
 * in French), and a fee as a grouped amount with two decimals and no symbol, for the caller to
 * pair with its currency code. Shared by the overview card and the partner details drawer so the
 * two never format the same term differently.
 */
export default function useProgramTermFormats() {
  const { i18n } = useTranslation();

  return useMemo(() => {
    const percent = new Intl.NumberFormat(i18n.language, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    const amount = new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return {
      /** A rate in whole percent, as the API sends it ("20.00"), not a fraction. */
      formatRevShare: (rate: number): string => percent.format(rate / 100),
      formatAmount: (value: number): string => amount.format(value),
    };
  }, [i18n.language]);
}
